/**
 * Each page's complete head from the data the page loads: one function per
 * page type, called by the page (through `<PageHead/>`) and by the SEO server
 * (`server/routes.mjs`, web plan W1.3) with the same query results, so the
 * head the server sends before JavaScript runs is the one the SPA renders.
 *
 * Pure (no React, no `window`): the formulas are `./templates`, the JSON-LD
 * `./structuredData`, the share images `@/utils/safeImageUrl`'s `og` preset
 * (1200×630 JPEG, `fmt_jpeg`).
 *
 * Every function takes the catalogue's `t` (web plan W3.0): a page passes its
 * locale route's (`useTranslate()`), the SEO server `tFor(route locale)`;
 * both default to DEFAULT_LOCALE's, so the words are the same catalogue's.
 */
import { tFor, type Translate } from '@/i18n';
import type { Brand, Promotion, Shop, Store } from '@/types';
import { getPromotionImageUrl, ImagePresets, safeImageUrl } from '@/utils/safeImageUrl';
import { DEFAULT_LOCALE, type Locale } from './locales';
import { brandSeoName } from './text';
import { saleCalendarPath, saleEventPath, publishedSaleEvents, type SaleEvent } from './saleEvents';
import {
  buildBrandLd,
  buildCollectionPageLd,
  buildListPageLd,
  buildPromotionLd,
  buildShopLd,
  buildStoreLd,
} from './structuredData';
import {
  categoryHubLabel,
  categoryHubPromotionsOn,
  linkedFromHubs,
  sitePath,
  topCategoryBrands,
  type CategoryHubPage,
} from './categoryHubs';
import {
  brandCityBrandName,
  brandCityCityName,
  type BrandCityHubPage,
} from './brandCityHubs';
import {
  cityHubLastPage,
  cityHubName,
  cityHubPromotionsOn,
  type CityHubPage,
} from './cityHubs';
import {
  brandCityHead,
  brandHead,
  brandsListHead,
  categoryHead,
  cityHead,
  entityAlternates,
  fixedPageHead,
  homeHead,
  promotionH1,
  promotionHead,
  promotionsListHead,
  shopsListHead,
  saleCalendarHead,
  saleEventHead,
  saleEventName,
  shopHead,
  SITE_NAME,
  storeHead,
  titledHead,
  type SeoAlternate,
  type SeoHead,
} from './templates';
import {
  isTrustPageLive,
  OWNER_TRUST_CONTENT,
  trustDescription,
  trustPagePath,
  type TrustContent,
  type TrustPageKey,
} from './trustPages';
import { catalogPath, entityPath } from './urls';

/** What every page head takes besides its data. */
export interface PageHeadOptions {
  /** The moment "active now" is judged at. */
  now?: Date;
  /** The catalogue's `t` of the page's locale (see the module comment). */
  t?: Translate;
  /**
   * The page's locale (its route's `/{cc}/{lang}`): the country in titles and
   * descriptions, the canonical and breadcrumb paths, the time zone dates and
   * `unavailable_after` are in, the JSON-LD's country. Defaults to
   * DEFAULT_LOCALE's, for the un-prefixed pages.
   */
  locale?: Locale;
}

/** How many promotions the home page lists first (its "Today" rule reads them). */
export const HOME_FEATURED_LIMIT = 6;

/** How many brands and shops the home page's rails show (the SEO server reads the same pages, web plan W4.5). */
export const HOME_RAIL_LIMIT = 10;

/** How many live deals /nearby lists above its map (the SEO server reads the same page, web plan W4.5). */
export const NEARBY_LIVE_DEALS_LIMIT = 4;

/** The home page: "Today" only while one of its featured promotions is live. */
export function homePageHead(featured: readonly Promotion[], options: PageHeadOptions = {}): SeoHead {
  return homeHead({ listed: featured }, { now: options.now, t: options.t, locale: options.locale });
}

// ─── Entity pages ──────────────────────────────────────────────────────────

/**
 * A brand page (`brandById`): the brand formula (web plan W0.6), its cover or
 * logo as the share image, a CollectionPage about the brand's Organization (W0.7).
 */
export function brandPageHead(brand: Brand, options: PageHeadOptions = {}): SeoHead {
  // Prefer the backend-composed share card (1200×630) when present (W3.9);
  // fall back to the CDN crop of the cover / logo.
  const image = brand.ogImageUrl ?? safeImageUrl(brand.coverImage ?? brand.logo, ImagePresets.og);
  return brandHead(
    { ...brand, stores: brand.brandStores },
    {
      now: options.now,
      t: options.t,
      locale: options.locale,
      image,
      imageSize: brand.ogImageUrl ? { width: 1200, height: 630 } : null,
      jsonLd: buildBrandLd(brand, { logo: safeImageUrl(brand.logo, ImagePresets.logo), locale: options.locale }),
      alternates: entityAlternates(brand.seo),
    },
  );
}

/** A shop page (`shopById`): the brand hub's formulas with its own name, a Store with its PostalAddress. */
export function shopPageHead(shop: Shop, options: PageHeadOptions = {}): SeoHead {
  return shopHead(shop, {
    now: options.now,
    t: options.t,
    locale: options.locale,
    image: safeImageUrl(shop.coverImage ?? shop.logo, ImagePresets.og),
    jsonLd: buildShopLd(shop, { logo: safeImageUrl(shop.logo, ImagePresets.logo), locale: options.locale }),
    alternates: entityAlternates(shop.seo),
  });
}

/** A store page (`storeById`): "{Brand} – {Area}, {City}", a Store part of its brand; `noindex, follow` unless the backend's `seo.robots` says otherwise. */
export function storePageHead(store: Store, options: Pick<PageHeadOptions, 't' | 'locale'> = {}): SeoHead {
  return storeHead(store, {
    t: options.t,
    locale: options.locale,
    image: safeImageUrl(store.coverImage ?? store.logo, ImagePresets.og),
    // SP14 (audit F14) is deferred: the Store's `name` is still the raw backend name, not the H1's
    // `storeDisplayName(store, options)`. Switching changes the golden `page.store` heads (plan note).
    jsonLd: buildStoreLd(store, { logo: safeImageUrl(store.logo, ImagePresets.logo), locale: options.locale }),
    alternates: entityAlternates(store.seo),
  });
}

/**
 * A promotion page (`promotionById`) at `now`: the promotion formula and the
 * D-4 robots, its poster as the share image, a WebPage whose mainEntity is
 * the Offer (availability from the dates).
 */
export function promotionPageHead(promo: Promotion, options: PageHeadOptions = {}): SeoHead {
  const now = options.now ?? new Date();
  // Prefer the backend-composed share card (1200×630) when present (W3.9);
  // fall back to the CDN crop of the promotion's poster image.
  const image = promo.ogImageUrl ?? getPromotionImageUrl(promo.media, ImagePresets.og);
  return promotionHead(promo, {
    now,
    t: options.t,
    locale: options.locale,
    image,
    imageSize: promo.ogImageUrl ? { width: 1200, height: 630 } : null,
    jsonLd: buildPromotionLd(promo, { image, now, locale: options.locale }),
    alternates: entityAlternates(promo.seo),
  });
}

// ─── Catalog lists (web plan W0.11) ────────────────────────────────────────

export interface ListPageInput<T> {
  /** The items of the page the URL names (page N's first). */
  items: readonly T[];
  totalRecords: number | null;
  /** The page the URL names (1-based; 1 for an invalid value). */
  page: number;
  /** The page for the title and canonical: N ≥ 2 adds " – Page N" and `?page=N`. */
  seoPage?: number;
  now?: Date;
  t?: Translate;
  /** The page's locale (see PageHeadOptions). */
  locale?: Locale;
}

/** `/brands`: the list formula and a CollectionPage whose ItemList is this page's brands. */
export function brandsListPageHead(input: ListPageInput<Brand>): SeoHead {
  const basePath = catalogPath(input.locale, '/brands');
  return brandsListHead(
    { totalCount: input.totalRecords },
    {
      now: input.now,
      t: input.t,
      locale: input.locale,
      page: input.seoPage,
      jsonLd: buildListPageLd({
        basePath,
        page: input.page,
        items: input.items,
        totalRecords: input.totalRecords,
        entry: (brand) => ({ name: brand.name, path: entityPath(brand, 'brand') }),
        locale: input.locale,
      }),
    },
  );
}

/**
 * `/promotions`: "({Mon YYYY})" only while a listed promotion is live; entries named by their H1.
 * `noindex, follow` while the backend reports none (an empty list is a thin page,
 * like `/shops`; web plan SP5 / audit F5): the page stays a 200 with its empty
 * state and is indexable again as soon as there is one.
 */
export function promotionsListPageHead(input: ListPageInput<Promotion> & { noindex?: boolean }): SeoHead {
  const basePath = catalogPath(input.locale, '/promotions');
  return promotionsListHead(
    {
      listed: input.items,
      totalCount: input.totalRecords,
      noindex: input.totalRecords === 0 || Boolean(input.noindex),
    },
    {
      now: input.now,
      t: input.t,
      locale: input.locale,
      page: input.seoPage,
      jsonLd: buildListPageLd({
        basePath,
        page: input.page,
        items: input.items,
        totalRecords: input.totalRecords,
        entry: (promo) => ({
          name: promotionH1(promo, { t: input.t, locale: input.locale }),
          path: entityPath(promo, 'promotion'),
        }),
        locale: input.locale,
      }),
    },
  );
}

/** The `/shops` title and description in DEFAULT_LOCALE's language (the page's own `t` wins). */
// Legacy, pinned by golden fixtures (src/seo/__fixtures__).
export const SHOPS_LIST_TITLE = tFor(DEFAULT_LOCALE)('shops:head.title');
export const SHOPS_LIST_DESCRIPTION = tFor(DEFAULT_LOCALE)('shops:head.description', { site: SITE_NAME });

/**
 * `/shops`: the list formula ("Shops in {Country}: Deals & Live Sales") and a
 * CollectionPage whose ItemList is this page's shops; `noindex, follow` while
 * the backend reports no shops (master D-5, web plan W0.3) or for a page that
 * does not exist.
 */
export function shopsListPageHead(input: ListPageInput<Shop> & { noindex?: boolean }): SeoHead {
  const basePath = catalogPath(input.locale, '/shops');
  return shopsListHead(
    { totalCount: input.totalRecords, noindex: input.totalRecords === 0 || Boolean(input.noindex) },
    {
      now: input.now,
      t: input.t,
      locale: input.locale,
      page: input.seoPage,
      jsonLd: buildListPageLd({
        basePath,
        page: input.page,
        items: input.items,
        totalRecords: input.totalRecords,
        entry: (shop) => ({ name: shop.name, path: entityPath(shop, 'shop') }),
        locale: input.locale,
      }),
    },
  );
}

// ─── Sale pages (web plan W0.15) ───────────────────────────────────────────

/** An event hub: its formula and a CollectionPage whose ItemList is the offers on screen. */
export function saleEventPageHead(
  event: SaleEvent,
  offers: { items: readonly Promotion[]; totalRecords: number | null },
  locale: Locale = DEFAULT_LOCALE,
  options: Pick<PageHeadOptions, 't'> & { alternates?: readonly SeoAlternate[] | null } = {},
): SeoHead {
  const t = options.t ?? tFor(locale);
  return saleEventHead(event, {
    locale,
    t,
    // The same event in another country (backend `seo.alternates`, `entityAlternates`).
    alternates: options.alternates ?? null,
    jsonLd: buildCollectionPageLd({
      path: saleEventPath(event, locale),
      items: offers.items.map((promo) => ({
        name: promotionH1(promo, { locale, t }),
        path: entityPath(promo, 'promotion'),
      })),
      numberOfItems: offers.totalRecords,
      webPageWhenEmpty: true,
      locale,
    }),
  });
}

/** The sale calendar: every published event hub, in date order. */
export function saleCalendarPageHead(
  locale: Locale = DEFAULT_LOCALE,
  options: Pick<PageHeadOptions, 't'> & { events?: readonly SaleEvent[] } = {},
): SeoHead {
  const t = options.t ?? tFor(locale);
  const events = options.events ?? publishedSaleEvents();
  return saleCalendarHead(events, {
    locale,
    t,
    jsonLd: buildCollectionPageLd({
      path: saleCalendarPath(locale),
      items: events.map((event) => ({
        name: saleEventName(event, { locale, t }),
        path: saleEventPath(event, locale),
      })),
      numberOfItems: events.length,
      locale,
    }),
  });
}

// ─── Category hubs (web plan W3.1) ─────────────────────────────────────────

/**
 * A category hub (backend `categoryPage`) on its page `page` of live sales:
 * the category formula ("{Label} in {Country} Today: {Top 3 brands}" while a
 * listed sale is live), " – Page N" and `?page=N` for N ≥ 2, robots from the
 * backend's `seo.robots`, a staff `seoOverrides` title/description (backend
 * B3.4) instead of the formula (the title still gets " – Page N" on page
 * N ≥ 2, so no two pages share one), the parent hub in the trail only while
 * it is indexable, and a CollectionPage whose ItemList is the page's sales.
 */
export function categoryPageHead(
  hub: CategoryHubPage,
  options: PageHeadOptions & { page?: number; locale?: Locale } = {},
): SeoHead {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const t = options.t ?? tFor(locale);
  const page = Number.isSafeInteger(options.page) && (options.page ?? 1) >= 1 ? (options.page as number) : 1;
  const { category } = hub;
  const parent = category.parentCategory;
  const parentPath = sitePath(parent?.urlPath);
  const parents = parent && parentPath && linkedFromHubs(parent) ? [{ name: categoryHubLabel(parent), path: parentPath }] : [];
  const overrideTitle = category.seoOverrides?.title?.trim();
  const seoOverrides = category.seoOverrides
    ? {
        ...category.seoOverrides,
        title: overrideTitle && page >= 2 ? t('common:title.page', { title: overrideTitle, page }) : overrideTitle,
      }
    : null;
  return categoryHead(
    {
      label: categoryHubLabel(category),
      path: hub.urlPath,
      listed: hub.livePromotions,
      topBrands: topCategoryBrands(hub.livePromotions),
      parents,
      seoOverrides,
      seo: hub.seo,
    },
    {
      locale,
      now: options.now,
      t,
      page,
      jsonLd:
        // No live sale: the visible list is the hub's brands (the ones the page links).
        hub.livePromotions.length === 0 && page === 1
          ? buildCollectionPageLd({
              path: hub.urlPath,
              items: hub.brands.map((brand) => ({
                name: brandSeoName(brand) || brand.name,
                path: linkedFromHubs(brand) ? entityPath(brand, 'brand') : '',
              })),
              numberOfItems: hub.brandCount > 0 ? hub.brandCount : null,
              webPageWhenEmpty: true,
              locale,
            })
          : buildListPageLd({
              basePath: hub.urlPath,
              page,
              items: categoryHubPromotionsOn(hub, page),
              totalRecords: hub.livePromotions.length,
              entry: (promo) => ({ name: promotionH1(promo, { locale, t }), path: entityPath(promo, 'promotion') }),
              webPageWhenEmpty: true,
              locale,
            }),
    },
  );
}

// ─── Brand × city hubs (web plan W3.3) ────────────────────────────────────

/**
 * A brand × city hub head (web plan W3.3): the head the SPA page
 * (`BrandCityPage`) and the SEO server (`server/routes.mjs`) build from the
 * same `brandCityPage` answer. Title, H1 and description from
 * `brandCityHead`; robots from the backend's `seo.robots`.
 */
export function brandCityPageHead(
  hub: BrandCityHubPage,
  options: PageHeadOptions & { locale?: Locale } = {},
): SeoHead {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const t = options.t ?? tFor(locale);
  return brandCityHead(
    {
      brand: hub.brand,
      city: brandCityCityName(hub.city),
      storeCount: hub.storeCount,
      path: hub.urlPath,
      // Pass the backend's seo so its robots override the template's default.
      seo: hub.seo,
    },
    {
      ...options,
      locale,
      t,
      // The stores the page lists, each with its own page (stores without a
      // page are left out); WebPage instead while none is listed.
      jsonLd: buildCollectionPageLd({
        path: hub.urlPath,
        items: hub.stores.map((store) => ({
          name: store.displayName || store.name,
          path: sitePath(store.urlPath) || '',
        })),
        numberOfItems: hub.storeCount > 0 ? hub.storeCount : null,
        webPageWhenEmpty: true,
        locale,
      }),
    },
  );
}

// ─── City hubs (web plan W3.2) ─────────────────────────────────────────────

/**
 * A city hub head (web plan W3.2): the head the SPA page (`CityPage`) and the
 * SEO server (`server/routes.mjs`) build from the same `cityPage` answer, so
 * what the server sends before JavaScript is what the page shows.
 *
 * Title: "Sale on Brands in {City} Today ({Mon YYYY})" while a live sale is
 * listed; "…: Latest & Upcoming Offers, Stores" otherwise. H1 is always "Sale
 * on Brands in {City}" (web plan W3.2 requirement). Robots from `seo.robots`.
 */
export function cityPageHead(
  hub: CityHubPage,
  options: PageHeadOptions & { page?: number; locale?: Locale } = {},
): SeoHead {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const t = options.t ?? tFor(locale);
  const page = Number.isSafeInteger(options.page) && (options.page ?? 1) >= 1 ? (options.page as number) : 1;
  return cityHead(
    {
      city: cityHubName(hub.city),
      path: hub.urlPath,
      listed: hub.livePromotions,
      brandCount: hub.brandCount,
      seo: hub.seo,
    },
    {
      locale,
      now: options.now,
      t,
      page,
      jsonLd:
        // The visible list of a hub with no live sale is its brands A–Z (the
        // same entries, linked as the page links them); never an empty ItemList.
        hub.livePromotions.length === 0 && page === 1
          ? buildCollectionPageLd({
              path: hub.urlPath,
              items: hub.brands.map((row) => ({
                name: brandSeoName(row.brand) || row.brand.name,
                path:
                  (row.brandCityPath ? sitePath(row.brandCityPath) : null) ||
                  (linkedFromHubs(row.brand) ? entityPath(row.brand, 'brand') : ''),
              })),
              numberOfItems: hub.brandCount > 0 ? hub.brandCount : null,
              webPageWhenEmpty: true,
              locale,
            })
          : buildListPageLd({
              basePath: hub.urlPath,
              page,
              items: cityHubPromotionsOn(hub, page),
              totalRecords: hub.livePromotions.length,
              entry: (promo) => ({ name: promotionH1(promo, { locale, t }), path: entityPath(promo, 'promotion') }),
              webPageWhenEmpty: true,
              locale,
            }),
    },
  );
}

// ─── Trust pages (web plan W3.7) ───────────────────────────────────────────

/**
 * A trust page (About, Contact, How we verify deals, For businesses): a fixed
 * page (`fixedPageHead`: "{Title} | PromoStork", or the title as written when
 * it names the site; the H1 is the title) titled from the catalogue's `trust`
 * namespace, described by the owner's copy (./trustPages `trustDescription`),
 * canonical on its global path, `index, follow`. Null while the page is not
 * live (./trustPages): it then does not exist, and the caller answers 404.
 */
export function trustPageHead(
  key: TrustPageKey,
  options: Pick<PageHeadOptions, 't'> & { content?: TrustContent } = {},
): SeoHead | null {
  const content = options.content ?? OWNER_TRUST_CONTENT;
  if (!isTrustPageLive(key, content)) return null;
  const t = options.t ?? tFor(DEFAULT_LOCALE);
  return fixedPageHead(
    {
      title: t(`trust:${key}.title`, { site: SITE_NAME }),
      description: trustDescription(content.copy[key]),
      path: trustPagePath(key),
    },
    { t },
  );
}
